import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Terminal, ArrowRight } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function goGoogleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-10 relative overflow-hidden border-r border-white/5">
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
        <Link to="/" data-testid="login-brand" className="flex items-center gap-2 relative">
          <div className="h-7 w-7 rounded-sm flex items-center justify-center bg-[var(--accent)]/10 border border-[var(--accent)]/40">
            <Terminal size={14} className="text-[var(--accent)]" />
          </div>
          <span className="font-display font-bold tracking-tight text-white">MQL<span className="text-[var(--accent)]">4</span>Forge</span>
        </Link>
        <div className="relative">
          <h1 className="font-display text-4xl text-white leading-tight">
            The trading desk<br/>
            <span className="text-[var(--text-secondary)]">for people who ship.</span>
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-4 max-w-md">
            Compose, generate, and clone MQL4 Expert Advisors from one workbench.
          </p>
        </div>
        <div className="relative flex gap-3 text-xs font-mono text-[var(--text-muted)]">
          <span className="chip">SESSION · SECURE</span>
          <span className="chip chip-accent">v1.0</span>
        </div>
      </div>

      <div className="flex flex-col justify-center p-8 md:p-14 max-w-lg w-full">
        <h2 className="font-display text-3xl text-white" data-testid="login-title">Log in</h2>
        <p className="text-sm text-[var(--text-secondary)] mt-1">Continue where you left off.</p>

        <button data-testid="login-google-btn" onClick={goGoogleLogin} className="mt-8 btn-outline w-full flex items-center justify-center gap-3 py-3">
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 5.04c1.78 0 3.37.61 4.63 1.81l3.42-3.42C17.95 1.45 15.24.5 12 .5 7.38.5 3.39 3.15 1.44 7.02l4 3.1C6.4 7.25 8.98 5.04 12 5.04z"/><path fill="#4285F4" d="M23.5 12.27c0-.79-.07-1.55-.19-2.27H12v4.51h6.47c-.28 1.5-1.12 2.78-2.39 3.63l3.85 2.99c2.25-2.08 3.57-5.13 3.57-8.86z"/><path fill="#FBBC05" d="M5.44 14.12a7.05 7.05 0 010-4.24l-4-3.1a11.5 11.5 0 000 10.44l4-3.1z"/><path fill="#34A853" d="M12 23.5c3.24 0 5.95-1.07 7.93-2.9l-3.85-2.99c-1.07.72-2.44 1.15-4.08 1.15-3.02 0-5.6-2.21-6.56-5.08l-4 3.1C3.39 20.85 7.38 23.5 12 23.5z"/></svg>
          Continue with Google
        </button>

        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 h-px bg-white/10"></div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">or with email</span>
          <div className="flex-1 h-px bg-white/10"></div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label-tiny">Email</label>
            <input data-testid="login-email-input" type="email" required value={email}
              onChange={(e)=>setEmail(e.target.value)} className="input-dark" placeholder="you@domain.com"/>
          </div>
          <div>
            <label className="label-tiny">Password</label>
            <input data-testid="login-password-input" type="password" required minLength={6} value={password}
              onChange={(e)=>setPassword(e.target.value)} className="input-dark" placeholder="••••••••"/>
          </div>
          <button data-testid="login-submit-btn" disabled={busy} type="submit"
            className="btn-primary w-full flex items-center justify-center gap-2 py-3">
            {busy ? "Signing in…" : (<>Log in <ArrowRight size={14}/></>)}
          </button>
        </form>

        <p className="text-sm text-[var(--text-secondary)] mt-6">
          No account? <Link to="/signup" data-testid="login-goto-signup" className="text-[var(--accent)] hover:underline">Create one</Link>
        </p>
      </div>
    </div>
  );
}
